import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import HotelMarketInsightTablesCellsTdBucketColor from 'lumen/components/hotel/market-insight/tables/cells/td-bucket-color';
import HotelMarketInsightTablesCellsTdCurrentPrice from 'lumen/components/hotel/market-insight/tables/cells/td-current-price';
import HotelMarketInsightTablesCellsTdHotelName from 'lumen/components/hotel/market-insight/tables/cells/td-hotel-name';
import HotelMarketInsightTablesCellsTdUsualPrice from 'lumen/components/hotel/market-insight/tables/cells/td-usual-price';
import type { MarketInsightHotel } from 'lumen/utils/market-insight/market-insight-helpers';

interface HotelMarketInsightTablesTablePriceLevelDistributionDetailsSignature {
  Args: {
    model: {
      canShowFormattedCancellableText: boolean;
      demandLevelColorScaleNumberDow: number;
      formattedCancellableIcon: string;
      formattedRate: string;
      formattedRoomNameAndCancellableText: string;
      formattedUsualRateDow: string;
      hasUsualRateDowValue: boolean;
      hotel: MarketInsightHotel;
      rate: number;
      usualRateDow: number;
    }[];
    setHighlightedMarker: (markerId: number, isHovered: boolean) => void;
    usualRateDow: number;
  };
}

const COLUMNS = {
  DEMAND_LEVEL_COLOR_SCALE_NUMBER_DOW: 'demandLevelColorScaleNumberDow',
  HOTEL: 'hotel',
  USUAL_RATE_DOW: 'usual_rate_dow',
  CURRENT: 'current',
};

export default class HotelMarketInsightTablesTablePriceLevelDistributionDetails extends Component<HotelMarketInsightTablesTablePriceLevelDistributionDetailsSignature> {
  @service declare intl: Services['intl'];

  initialSorts = [
    {
      valuePath: COLUMNS.DEMAND_LEVEL_COLOR_SCALE_NUMBER_DOW,
      isAscending: false,
    },
  ];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    return [
      {
        name: '',
        valuePath: COLUMNS.DEMAND_LEVEL_COLOR_SCALE_NUMBER_DOW,
        minWidth: 20,
        width: 20,
        maxWidth: 20,
        tdComponent: HotelMarketInsightTablesCellsTdBucketColor,
        // Note: This class is necessary to remove the padding around the bucket color column
        tdClassNames: 'bucket-color',
        textAlign: 'center' as const,
      },
      {
        name: this.intl.t('general.hotel.plural', { count: 1 }),
        valuePath: `${COLUMNS.HOTEL}.value`,
        displayValuePath: `${COLUMNS.HOTEL}.formattedValue`,
        width: 150,
        isSortable: false,
        tdComponent: HotelMarketInsightTablesCellsTdHotelName,
        textAlign: 'left' as const,
        setHighlightedMarker: this.args.setHighlightedMarker,
      },
      {
        name: this.intl.t('market_insight.usual_dow', {
          DOW: this.args.usualRateDow,
        }),
        valuePath: `${COLUMNS.USUAL_RATE_DOW}.value`,
        displayValuePath: `${COLUMNS.USUAL_RATE_DOW}.formattedValue`,
        intWidth: 100,
        textAlign: 'left' as const,
        tdComponent: HotelMarketInsightTablesCellsTdUsualPrice,
      },
      {
        name: this.intl.t('revenue_insight.legend.current'),
        valuePath: `${COLUMNS.CURRENT}.value`,
        displayValuePath: `${COLUMNS.CURRENT}.formattedValue`,
        width: 100,
        textAlign: 'left' as const,
        tdComponent: HotelMarketInsightTablesCellsTdCurrentPrice,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    if (!this.args.model) {
      return [];
    }

    return this.args.model.map(
      ({
        demandLevelColorScaleNumberDow,
        formattedCancellableIcon,
        canShowFormattedCancellableText,
        formattedRoomNameAndCancellableText,
        hotel,
        rate,
        hasUsualRateDowValue,
        formattedRate,
        usualRateDow,
        formattedUsualRateDow,
      }) => {
        return {
          className: hotel?.isOwnHotel ? 'own-hotel-row' : '',
          [COLUMNS.DEMAND_LEVEL_COLOR_SCALE_NUMBER_DOW]:
            demandLevelColorScaleNumberDow,
          [COLUMNS.HOTEL]: {
            value: hotel,
            formattedValue: hotel.name,
          },
          [COLUMNS.CURRENT]: {
            value: rate,
            formattedValue: {
              formattedRate,
              formattedCancellableIcon,
              canShowFormattedCancellableText,
              formattedRoomNameAndCancellableText,
            },
          },
          [COLUMNS.USUAL_RATE_DOW]: {
            value: usualRateDow,
            formattedValue: {
              formattedUsualRateDow,
              hasUsualRateDowValue,
              formattedRoomNameAndCancellableText,
            },
          },
        };
      },
    );
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @enableAutoSortsUpdate={{true}}
      @initialSorts={{this.initialSorts}}
      @removeTableBorder={{true}}
      @rows={{this.rows}}
      class="price-level-distribution-details-compset-table"
    />
  </template>
}
